import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';

import {
  ACCESS_TOKEN_COOKIE_NAME,
  ADMIN_USER_COOKIE_NAME,
  MD_AUTH_LOCAL_STORAGE_KEY,
} from '~/common/constants/auth';
import { ACCOUNT_PAGE_SLUG } from '~/common/constants/pageLayout';
import { MobileNumberDialog } from '~/components/AuthenticationProvider/MobileNumberDialog/MobileNumberDialog';
import { AuthApi } from '~/services/auth/authApi';
import { RestApi } from '~/services/restApi';
import { UserApi } from '~/services/user/UserApi';
import { setLocalStorage } from '~/utils/localStorage';

import { AuthDialog } from './AuthDialog/AuthDialog';
import { EmailLoginDialog } from './EmailLoginDialog/EmailLoginDialog';
import { EmailSignUpDialog } from './EmailSignUpDialog/EmailSignUpDialog';
import { SocialSignUpDialog } from './SocialSignUpDialog/SocialSignUpDialog';
import { getGrantTypeFromAuthMethod } from './utils';
import { alias, identify, trackEvent } from '../analytics/analytics';
import {
  LOGGED_IN_FE_EVENT_NAME,
  LOGIN_MODAL_VIEWED_EVENT_NAME,
  SIGNED_UP_FE_EVENT_NAME,
  USER_LOG_IN_COMPLETED_EVENT_NAME,
  USER_SIGNUP_COMPLETED_EVENT_NAME,
} from '../analytics/constants';
import {
  getSafeRedirectUrl,
  SITTER_APPLICATION_ENTRY_POINT,
  SITTER_APPLICATION_FALLBACK_URL,
} from '../pages/SignIn/utils/utils';

import type { AuthMethods } from './constants';
import type {
  OnLoginSuccessHandler,
  OnOpenAuthDialogHandler,
  OnSignUpSuccessHandler,
  OnSocialSignUpHandler,
} from './types';
import type { ReactElement, ReactNode } from 'react';
import type { UserDetails } from '~/common/types/user';
import type {
  GetAuthTokenRESTRequest,
  LoginSocialNewUserRestResponse,
} from '~/services/auth/types';

type Props = {
  children: ReactNode;
  route: string;
  userDetails: UserDetails | null;
};

export type Context = {
  getUserDetails: () => void;
  isAuthDialogOpen: boolean;
  isEmailLoginDialogOpen: boolean;
  isEmailSignUpDialogOpen: boolean;
  isMobileNumberDialogOpen: boolean;
  isSocialSignUpDialogOpen: boolean;
  isUserDetailsLoading: boolean;
  isUserLoggedIn: boolean | null;
  isUserSitter: boolean;
  loginAsAdmin: (impersonationToken: string, onSuccess: () => void) => Promise<void>;
  openAuthDialog: OnOpenAuthDialogHandler;
  setEmail: (email: string) => void;
  setEmailLoginDialogVisible: (isVisible: boolean) => void;
  setIsAuthDialogOpen: (isOpen: boolean) => void;
  setIsEmailSignUpDialogOpen: (isOpen: boolean) => void;
  setIsMobileNumberDialogOpen: (isOpen: boolean) => void;
  setIsSocialSignUpDialogOpen: (isOpen: boolean) => void;
  setIsUserLoggedIn(isLoggedIn: boolean): void;
  user: UserDetails | null;
};

export const AuthenticationContext = createContext<Context>({
  isAuthDialogOpen: false,
  openAuthDialog: () => {},
  setEmail: () => {},
  setEmailLoginDialogVisible: () => {},
  isUserDetailsLoading: true,
  isUserSitter: false,
  user: null,
  getUserDetails: () => {},
  isUserLoggedIn: false,
  setIsUserLoggedIn: () => {},
  setIsMobileNumberDialogOpen: () => {},
  setIsAuthDialogOpen: () => {},
  isMobileNumberDialogOpen: false,
  isSocialSignUpDialogOpen: false,
  setIsSocialSignUpDialogOpen: () => {},
  isEmailLoginDialogOpen: false,
  setIsEmailSignUpDialogOpen: () => {},
  isEmailSignUpDialogOpen: false,
  loginAsAdmin: async () => {},
});

export const AuthenticationProvider = ({ children, userDetails }: Props): ReactElement => {
  const [title, setTitle] = useState('');
  const [promoQueryParam, setPromoQueryParam] = useState('');
  const [onLoginSuccessRedirectUrl, setOnLoginSuccessRedirectUrl] = useState('');
  const [socialSignUpData, setSocialSignUpData] = useState<
    (LoginSocialNewUserRestResponse & { authMethod: AuthMethods }) | null
  >(null);

  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [isEmailSignUpDialogOpen, setIsEmailSignUpDialogOpen] = useState(false);
  const [isMobileNumberDialogOpen, setIsMobileNumberDialogOpen] = useState(false);
  const [isSocialSignUpDialogOpen, setIsSocialSignUpDialogOpen] = useState<boolean>(false);
  const [isEmailLoginDialogOpen, setEmailLoginDialogVisible] = useState<boolean>(false);

  const [email, setEmail] = useState<string>('');
  // The server already loaded the user when there is a valid session. Start
  // from that so the first render (and the server HTML) shows the logged-in
  // header instead of the logged-out one until the cookie check below runs.
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean | null>(userDetails ? true : null);
  const [user, setUser] = useState<UserDetails | null>(userDetails);

  const { query: routerQuery } = useRouter();

  const loginSuccessCallbackRef = useRef<
    ((userData: { email: string; userId: number }) => void) | null
  >(null);
  const signupSuccessCallbackRef = useRef<
    ((userData: { email: string; userId: number }) => void) | null
  >(null);
  const validatePhoneSuccessCallbackRef = useRef<((userID: number) => void) | undefined>();

  const router = useRouter();

  useEffect(() => {
    // initialize isUserLoggedIn
    const isAuthCookiesProvided = !!Cookies.get(ACCESS_TOKEN_COOKIE_NAME);
    setIsUserLoggedIn(isAuthCookiesProvided);
  }, []);

  useEffect(() => {
    if (user) {
      identify(user.id, { isSitter: user.isSitter, email: user.email });
    }
  }, [user]);

  const getUserDetails = useCallback((): void => {
    UserApi.getUserDetails().then((data) => {
      setUser(data);
    });
  }, []);

  const openAuthDialog = (
    data: {
      queryParam?: string;
      shouldValidatePhone?: boolean;
      successRedirectUrl?: string;
      title: string;
    },
    loginSuccessCallback?: (userData: { email: string; userId: number }) => void,
    signupSuccessCallback?: (userData: { email: string; userId: number }) => void,
    validatePhoneSuccessCallback?: (userID: number) => void
  ): void => {
    const { title: newTitle, successRedirectUrl, queryParam, shouldValidatePhone } = data;

    trackEvent(LOGIN_MODAL_VIEWED_EVENT_NAME);
    setIsAuthDialogOpen(true);
    setTitle(newTitle);
    setOnLoginSuccessRedirectUrl(successRedirectUrl ?? '');
    setPromoQueryParam(queryParam ?? '');
    loginSuccessCallbackRef.current = loginSuccessCallback ?? null;
    signupSuccessCallbackRef.current = signupSuccessCallback ?? null;
    if (shouldValidatePhone) {
      validatePhoneSuccessCallbackRef.current = validatePhoneSuccessCallback;
    }
  };

  const onLoginSuccess = async (
    data: Parameters<OnLoginSuccessHandler>[0],
    userData: Parameters<OnLoginSuccessHandler>[1],
    authMethod: Parameters<OnLoginSuccessHandler>[2]
  ): Promise<void> => {
    await identify(userData.userId, {});

    Cookies.set(ACCESS_TOKEN_COOKIE_NAME, JSON.stringify({ tokens: data }) ?? '', {
      expires: data.expires_in / 86400,
    });
    setLocalStorage(MD_AUTH_LOCAL_STORAGE_KEY, { ...data });
    RestApi.setAuthorizationHeader({ authorization: `Bearer ${data.access_token}` });
    getUserDetails();
    setIsUserLoggedIn(true);

    trackEvent(USER_LOG_IN_COMPLETED_EVENT_NAME, {
      type: authMethod,
      user_id: userData.userId,
      email: userData.email,
      user_channel: 'website',
      grant_type: getGrantTypeFromAuthMethod(authMethod),
    });

    // This is added for maintain legacy events in Mixpanel
    trackEvent(LOGGED_IN_FE_EVENT_NAME, {
      type: authMethod,
      id: userData.userId,
      email: userData.email,
      grant_type: getGrantTypeFromAuthMethod(authMethod),
      sms_verification_required: false,
    });

    if (loginSuccessCallbackRef.current) {
      setTimeout(() => {
        loginSuccessCallbackRef.current?.(userData);
      }, 500);
    }

    setTimeout(() => {
      if (onLoginSuccessRedirectUrl === 'false') {
        return;
      }

      if (!onLoginSuccessRedirectUrl) {
        if (routerQuery?.type === 'sitter') {
          router.push(
            `${SITTER_APPLICATION_ENTRY_POINT}?entityId=${userData.userId}&fallbackUrl=${SITTER_APPLICATION_FALLBACK_URL}`
          );
          return;
        }

        if (routerQuery?.redirectUrl && typeof routerQuery?.redirectUrl === 'string') {
          router.push(getSafeRedirectUrl(routerQuery?.redirectUrl));
          return;
        }

        router.push(ACCOUNT_PAGE_SLUG);
        return;
      }

      router.push(onLoginSuccessRedirectUrl);
    }, 500);
  };

  const onSocialSignUp: OnSocialSignUpHandler = (data, authMethod) => {
    setSocialSignUpData({ ...data, authMethod });
    setIsSocialSignUpDialogOpen(true);
  };

  const onSignUpSuccess: OnSignUpSuccessHandler = async (
    data,
    userData,
    authMethod,
    signUpTrackingData
  ) => {
    // Signup can happen directly from the anonymous session, so link that
    // session before recording the signup and subsequent authenticated events.
    alias(userData.userId);
    await identify(userData.userId, {
      email: userData.email,
      receive_special_offers: signUpTrackingData.receiveSpecialOffers,
    });

    trackEvent(USER_SIGNUP_COMPLETED_EVENT_NAME, {
      type: authMethod,
      promo: promoQueryParam,
      user_id: userData.userId,
      user_channel: 'website',
      email: userData.email,
      sms_verification_required: false,
      grant_type: getGrantTypeFromAuthMethod(authMethod),
      receive_special_offers: signUpTrackingData.receiveSpecialOffers,
    });

    trackEvent(SIGNED_UP_FE_EVENT_NAME, {
      type: authMethod,
      promo: promoQueryParam,
      id: userData.userId,
      user_channel: 'website',
      email: userData.email,
      sms_verification_required: false,
      grant_type: getGrantTypeFromAuthMethod(authMethod),
      receive_special_offers: signUpTrackingData.receiveSpecialOffers,
    });

    await onLoginSuccess(data, userData, authMethod);

    if (signupSuccessCallbackRef.current) {
      setTimeout(() => {
        signupSuccessCallbackRef.current?.(userData);
      }, 500);
    }

    if (onLoginSuccessRedirectUrl && onLoginSuccessRedirectUrl !== 'false') {
      setTimeout(() => {
        router.push(onLoginSuccessRedirectUrl);
      }, 500);
    }
  };

  const loginAsAdmin = useCallback(
    async (impersonationToken: string, onSuccess: () => void): Promise<void> => {
      if (!impersonationToken) {
        return;
      }

      const credentials = { impersonationToken };
      try {
        const data = await AuthApi.getAuthToken(credentials as GetAuthTokenRESTRequest);
        Cookies.set(ACCESS_TOKEN_COOKIE_NAME, JSON.stringify({ tokens: data }) ?? '', {
          expires: data.expires_in / 86400,
        });
        setLocalStorage(MD_AUTH_LOCAL_STORAGE_KEY, { ...data });
        RestApi.setAuthorizationHeader({ authorization: `Bearer ${data.access_token}` });

        getUserDetails();

        const expires = `${dayjs().add(1, 'day').format('ddd, DD MMM YYYY HH:MM:SS')} GMT`;
        Cookies.set(ADMIN_USER_COOKIE_NAME, '1', { expires: new Date(expires) });

        setIsUserLoggedIn(true);

        onSuccess();
      } catch {
        // Handle error silently
      }
    },
    [getUserDetails]
  );

  return (
    <AuthenticationContext.Provider
      value={{
        isAuthDialogOpen,
        isSocialSignUpDialogOpen,
        openAuthDialog,
        setEmail,
        setEmailLoginDialogVisible,
        setIsEmailSignUpDialogOpen,
        setIsMobileNumberDialogOpen,
        setIsAuthDialogOpen,
        setIsSocialSignUpDialogOpen,
        isUserDetailsLoading: false,
        isUserSitter: user?.isSitter ?? false,
        user,
        getUserDetails,
        isUserLoggedIn,
        setIsUserLoggedIn,
        isMobileNumberDialogOpen,
        isEmailLoginDialogOpen,
        isEmailSignUpDialogOpen,
        loginAsAdmin,
      }}
    >
      <>
        {children}

        <AuthDialog onLoginSuccess={onLoginSuccess} onSocialSignUp={onSocialSignUp} title={title} />
        <EmailLoginDialog email={email} onLoginSuccess={onLoginSuccess} />
        <EmailSignUpDialog email={email} onSignUpSuccess={onSignUpSuccess} />
        <SocialSignUpDialog onSignUpSuccess={onSignUpSuccess} signUpData={socialSignUpData} />
        <MobileNumberDialog handleValidatePhoneSuccess={validatePhoneSuccessCallbackRef.current} />
      </>
    </AuthenticationContext.Provider>
  );
};
